import { Anchor, Box, Button, Card, CardBody, CardFooter, CardHeader, CheckBox, Text } from 'grommet';
import { Cut, Inspect } from 'grommet-icons';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AddNewButton,
  CommercialEditReviewModal,
  ConfirmModal,
  Delete,
  Divider,
  EditableText,
  SelectableText,
  TalonTip,
} from '../..';

import {
  Commercial,
  CommercialType,
  Edit,
  EditType,
  HoldingFeeCycleStatus,
  TCommercialId,
  TEditId,
  THoldingFeeCycleInfo,
  TUpdateCommercialRequest,
  TUpdateEditRequest,
} from '@talon/web/lib/api';
import {
  useCommercialStore,
  useEstimateStore,
  useHoldingFeeStore,
  usePerformerStore,
  useProjectStore,
} from '@talon/web/src/context';
import { borderRadiusSize } from 'src/theme';
import { USD } from 'src/utils/currency';
import { formatDateOnly } from 'src/utils/dateOnly';
import { CreateEditModal } from './CreateEditModal';
import { EditRow } from './EditRow';

export type CommercialEditRowProps = {
  commercialId: TCommercialId;
  onChecked: () => void;
  checked: boolean;
  initialEditId?: string;
};

export const CommercialEditRow = observer(
  ({ commercialId, onChecked, checked, initialEditId }: CommercialEditRowProps) => {
    /** Context **/
    const navigate = useNavigate();
    const estimateStore = useEstimateStore();
    const commercialStore = useCommercialStore();
    const performerStore = usePerformerStore();
    const holdingFeeStore = useHoldingFeeStore();
    const projectStore = useProjectStore();

    /** State **/
    const [commercial, setCommercial] = useState<Commercial>();
    const [showAddEditModal, setShowAddEditModal] = useState(false);
    const [pendingTypeChange, setPendingTypeChange] = useState<CommercialType>();
    const [selectedEditIds, setSelectedEditIds] = useState<TEditId[]>([]);
    const [showCommercialEditReviewModal, setShowCommercialEditReviewModal] = useState(false);
    const [wages, setWages] = useState(0);
    const [total, setTotal] = useState(0);
    const [expandedHoldingFeesContractId, setExpandedHoldingFeesContractId] = useState<string | null>(null);
    const [holdingFeeSchedules, setHoldingFeeSchedules] = useState<Record<string, THoldingFeeCycleInfo[]>>({});

    /** Refs **/
    const columnWidths = [
      '48px', // checkbox
      '21%', // name
      '20%', // type
      '8%', // duration
      '6%', // paid usage
      '19%', // adid/isci
      '224px', // asset
      '48px', // edit reviews button
    ];

    /** Computed **/
    const usageEstimate = estimateStore.getTempUsageEstimate(commercial?.id);

    /** Methods **/

    // Reads estimateStore.tempEstimate directly (rather than a value captured during
    // render) so this isn't tracked as a render-time MobX dependency - otherwise every
    // commercial's row re-renders whenever any one of them recalculates its estimate.
    const recalculateUsageEstimate = async () => {
      if (!commercial) return;

      const usageEstimate = estimateStore.tempEstimate?.usage_estimates.find(
        (usageEstimate) => usageEstimate.commercial?.id === commercial.id
      );

      if (usageEstimate) await estimateStore.updateUsageEstimate(usageEstimate.id, undefined, true);
    };

    const chooseEdit = (id: TEditId) => {
      if (selectedEditIds.includes(id)) {
        setSelectedEditIds(selectedEditIds.filter((s) => s !== id));
      } else {
        setSelectedEditIds([...selectedEditIds, id]);
      }
    };

    const updateCommercial = async (update: TUpdateCommercialRequest) => {
      if (!commercial) return;
      const updatedCommercial = await commercialStore.updateCommercial(commercial.id, update);

      if (updatedCommercial) {
        setCommercial(updatedCommercial);

        // Only the commercial's type affects usage fee calculations (changing it already
        // warns about resetting usages, below) - title/duration edits don't, so skip the
        // costly whole-project estimate refresh for those.
        if (update.type !== undefined) await recalculateUsageEstimate();
      }
    };

    const onChangeCommercialType = async (type: CommercialType) =>
      type !== commercial?.type && commercial?.usages.length
        ? setPendingTypeChange(type)
        : await updateCommercial({ type: type });

    const onConfirmCommercialType = async (type: CommercialType) => {
      await updateCommercial({ type });
      setPendingTypeChange(undefined);
    };

    const addEdit = async (commercialId: TCommercialId, editType: EditType) => {
      if (!commercial) return;
      const edit = await commercialStore.createEdit({
        title: `${commercial.title} ${editType} Edit`,
        type: editType,
        commercialId,
        durationInSeconds: 0, // Default duration, can be updated later
      });
      if (edit && commercial) {
        const edits = [...commercial.edits, edit];
        const updatedCommercial = { ...commercial, edits };
        setCommercial(updatedCommercial as Commercial);
      }
      setShowAddEditModal(false);
    };

    const updateEdit = useCallback(
      async (update: Edit) => {
        if (!commercial) return;
        console.log('Updating edit:', update);

        // Build the update request, explicitly including undefined values as null for the API
        const updateRequest: TUpdateEditRequest = {};
        if (update.duration !== undefined) updateRequest.durationInSeconds = update.duration;
        if (update.ad_id !== undefined) updateRequest.adId = update.ad_id;
        if ('asset_id' in update) updateRequest.assetId = update.asset_id || null;
        if (update.is_paid !== undefined) updateRequest.isPaid = update.is_paid;
        if (update.title !== undefined) updateRequest.title = update.title;
        if (update.type !== undefined) updateRequest.type = update.type;

        const edit = await commercialStore.updateEdit(update.id, updateRequest);
        if (!edit) return;
        const edits = commercial.edits?.map((e) => (e.id === edit.id ? edit : e));
        const updatedCommercial = { ...commercial, edits };
        setCommercial(updatedCommercial);
      },
      [commercial, commercialStore]
    );

    const deleteEdits = async () => {
      if (!commercial || selectedEditIds.length === 0) return;

      await Promise.all(selectedEditIds.map((editId) => commercialStore.deleteEdit(editId)));

      const edits = commercial.edits.filter((edit) => !selectedEditIds.includes(edit.id));

      setCommercial({ ...commercial, edits });
      setSelectedEditIds([]);
    };

    const getUsageConsumedForRole = (roleId?: string): number => {
      if (!roleId || !usageEstimate) return 0;
      return usageEstimate.items
        .filter((item) => item.role?.id === roleId)
        .reduce((sum, item) => sum + item.subtotal, 0);
    };

    const guaranteeRollup = (commercial?.contracts ?? []).reduce(
      (acc, contract) => {
        const guarantee = contract.usage_guarantee_amount;
        if (!guarantee) return acc;

        const overage = getUsageConsumedForRole(contract.role?.id) - guarantee;
        if (overage > 0) {
          acc.performersOverGuarantee += 1;
          acc.totalOverage += overage;
        }
        return acc;
      },
      { performersOverGuarantee: 0, totalOverage: 0 }
    );

    const toggleHoldingFeeSchedule = async (contractId: string) => {
      if (expandedHoldingFeesContractId === contractId) {
        setExpandedHoldingFeesContractId(null);
        return;
      }

      setExpandedHoldingFeesContractId(contractId);
      if (!holdingFeeSchedules[contractId]) {
        const schedule = await holdingFeeStore.getSchedule(contractId);
        if (schedule) {
          setHoldingFeeSchedules((prev) => ({ ...prev, [contractId]: schedule.cycles }));
        }
      }
    };

    const updateHoldingFeeCycle = async (contractId: string, cycleNumber: number, status: HoldingFeeCycleStatus) => {
      const updatedCycle = await holdingFeeStore.updateCycle(contractId, cycleNumber, status);
      if (!updatedCycle) return;

      setHoldingFeeSchedules((prev) => ({
        ...prev,
        [contractId]: (prev[contractId] ?? []).map((cycle) =>
          cycle.cycleNumber === cycleNumber ? updatedCycle : cycle
        ),
      }));
    };

    const updateGuaranteeAmount = async (contractId: string, amount: number) => {
      if (!commercial) return;
      const updatedContract = await performerStore.updateUsageGuaranteeAmount(contractId, amount);
      if (!updatedContract) return;

      const contracts = commercial.contracts?.map((c) =>
        c.id === contractId ? { ...c, usage_guarantee_amount: amount } : c
      );
      setCommercial({ ...commercial, contracts });
    };

    /** Effects **/

    useEffect(() => {
      const getCommercial = async (id: TCommercialId) => {
        if (!projectStore.currentProject) return;
        const c = await commercialStore.getCommercial(id);
        if (c) setCommercial(c);
      };

      getCommercial(commercialId);
      // Depend on the project id, not the currentProject object itself - that object gets a
      // new reference on every enrichment/update, which would otherwise re-fetch this
      // commercial (and cascade into every row re-rendering) far more often than needed.
    }, [commercialId, projectStore.currentProject?.id]);

    // Auto-open edit review modal when initialEditId is provided (from notification)
    useEffect(() => {
      if (initialEditId && commercial) {
        setShowCommercialEditReviewModal(true);
      }
    }, [initialEditId, commercial]);

    useEffect(() => {
      if (usageEstimate) {
        let wages = 0;
        let total = 0;

        for (const item of usageEstimate.items) {
          wages += item.subtotal;
          total += item.subtotal_plus_fringe;
        }

        setWages(wages);
        setTotal(total);
      }
    }, [usageEstimate]);

    /** Render **/

    return (
      <Card round={borderRadiusSize} background="light-1" animation={{ type: 'fadeIn', duration: 500 }}>
        {/* Header */}
        <CardHeader
          justify="start"
          align="center"
          gap="small"
          background={{ color: 'brand-gradient', dark: true }}
          style={{ background: 'var(--brand-gradient)', zIndex: 1 }}
          height={{ max: '80px', min: '80px' }}
          pad={{ horizontal: 'small' }}
        >
          <Box direction="row" gap="small" pad="small" align="center">
            <CheckBox onChange={onChecked} checked={checked} color="white" />
          </Box>

          <Box direction="row" gap="small" pad="small" align="center">
            <EditableText
              textColor="white"
              val={commercial?.title ?? ''}
              onChange={(title) => updateCommercial({ title })}
            />
          </Box>

          <Box direction="row" gap="small" pad="small" align="center">
            <Text color="white">Type:</Text>
            <SelectableText
              textColor="white"
              options={Object.values(CommercialType).map((t) => ({ label: t, value: t }))}
              val={commercial?.type}
              onChange={(val) => onChangeCommercialType(val as CommercialType)}
            />
          </Box>

          <Box direction="row" gap="small" pad="small" align="center">
            <Text style={{ whiteSpace: 'nowrap' }} color="white">
              Duration:
            </Text>
            <Box direction="row" gap="xxsmall">
              <EditableText
                textColor="white"
                val={commercial?.duration?.toString() ?? ''}
                onChange={(val) => updateCommercial({ durationInSeconds: val ? parseInt(val) : 0 })}
              />
              <Text color="white">seconds</Text>
            </Box>
          </Box>

          <Box flex />

          <Box direction="row" justify="end" pad="small" gap="large">
            <Box direction="row" gap="xsmall" justify="end" align="center" width="">
              <Text size="small" weight={700} color="white">
                WAGES:
              </Text>
              {
                <Text weight="bold" color="white">
                  {USD(wages)}
                </Text>
              }
            </Box>
            <Box direction="row" gap="xsmall" justify="end" align="center" width="10rem">
              <Text size="small" weight={700} color="white">
                TOTAL:
              </Text>
              {
                <Text weight="bold" color="white">
                  {USD(total)}
                </Text>
              }
            </Box>
          </Box>
        </CardHeader>

        {/* MPU / Session Info */}
        {commercial?.last_shoot_date && (
          <Box
            direction="row"
            gap="medium"
            pad={{ vertical: 'xsmall', horizontal: 'small' }}
            background="light-3"
            border={{ side: 'bottom', size: '1px', color: 'light-4' }}
          >
            <Text size="xsmall" color="dark-4">
              Last Shoot Date: {formatDateOnly(commercial.last_shoot_date)}
            </Text>
            {commercial.mpu_start_date && commercial.mpu_end_date && (
              <Text size="xsmall" color="dark-4">
                MPU Window: {formatDateOnly(commercial.mpu_start_date)} –{' '}
                {formatDateOnly(commercial.mpu_end_date)}
              </Text>
            )}
          </Box>
        )}

        {/* Body */}
        <CardBody animation={{ type: 'slideDown', size: 'medium' }} background="white" elevation="xsmall">
          {/* Table Header */}
          <Box
            direction="row"
            justify="between"
            align="center"
            gap="medium"
            pad={{ vertical: 'xsmall', horizontal: 'small' }}
            background="light-2"
            animation={{ type: 'slideDown', size: 'medium', delay: 300 }}
            border={{ side: 'bottom', size: '1px', color: 'light-4' }}
          >
            <Box height="1rem" width={columnWidths[0]} />

            <Box width={columnWidths[1]}>
              <Text size="xsmall">EDIT NAME</Text>
            </Box>
            <Box width={columnWidths[2]}>
              <Text size="xsmall">EDIT TYPE</Text>
            </Box>
            <Box width={columnWidths[3]}>
              <Text size="xsmall">DURATION (sec)</Text>
            </Box>
            <Box width={columnWidths[4]} align="center">
              <Text size="xsmall">PAID USAGE</Text>
            </Box>
            <Box width={columnWidths[5]}>
              <Text size="xsmall">AD ID / ISCI</Text>
            </Box>
            <Box width={columnWidths[6]}>{/* <Text size="xsmall">ASSET</Text> */}</Box>

            <Box height="1rem" width={columnWidths[7]} />
          </Box>

          {(commercial?.edits ?? []).length > 0 ? (
            commercial?.edits?.map((edit, i) => (
              <Box key={edit.id}>
                {i > 0 && <Divider height="0.5px" color="light-3" />}
                <EditRow
                  edit={edit}
                  commercial={commercial}
                  onChecked={() => chooseEdit(edit.id)}
                  checked={selectedEditIds.includes(edit.id)}
                  updateEdit={(update) => updateEdit({ ...edit, ...update })}
                  columnWidths={columnWidths}
                />
              </Box>
            ))
          ) : (
            <Box pad="medium" align="center">
              <Text color="dark-3">
                No Edits found.{' '}
                <Anchor weight={300} onClick={() => setShowAddEditModal(true)} color="accent-5">
                  Click here to create one
                </Anchor>
                .
              </Text>
            </Box>
          )}

          {/* Cast */}
          <Box
            direction="row"
            align="center"
            justify="between"
            gap="medium"
            pad={{ vertical: 'xsmall', horizontal: 'small' }}
            background="light-2"
            border={{ side: 'bottom', size: '1px', color: 'light-4' }}
          >
            <Text size="xsmall">CAST</Text>
            {guaranteeRollup.performersOverGuarantee > 0 && (
              <Text size="xsmall" color="status-critical" weight="bold">
                {guaranteeRollup.performersOverGuarantee} performer
                {guaranteeRollup.performersOverGuarantee > 1 ? 's' : ''} over usage guarantee (
                {USD(guaranteeRollup.totalOverage)} total)
              </Text>
            )}
          </Box>

          {(commercial?.contracts ?? []).length > 0 ? (
            commercial?.contracts?.map((contract, i) => {
              const consumed = getUsageConsumedForRole(contract.role?.id);
              const guarantee = contract.usage_guarantee_amount;
              const overage = guarantee ? consumed - guarantee : 0;

              return (
                <Box key={contract.id}>
                  {i > 0 && <Divider height="0.5px" color="light-3" />}
                  <Box direction="row" align="center" gap="medium" pad={{ vertical: 'xsmall', horizontal: 'small' }}>
                    <Box width={columnWidths[1]}>
                      <Anchor size="small" onClick={() => navigate(`/performers/${contract.performer?.id}`)}>
                        {contract.performer?.first_name} {contract.performer?.last_name}
                      </Anchor>
                    </Box>
                    <Box width={columnWidths[2]}>
                      <Text size="small" color="dark-3">
                        {contract.role?.name}
                        {contract.role?.performer_type ? ` (${contract.role.performer_type})` : ''}
                      </Text>
                    </Box>
                    <Box direction="row" gap="xsmall" align="center">
                      <Text size="small">Usage Guarantee:</Text>
                      <EditableText
                        val={guarantee?.toString() ?? ''}
                        onChange={(val) => updateGuaranteeAmount(contract.id, val ? parseFloat(val) : 0)}
                        minValue={0}
                      />
                    </Box>
                    {guarantee ? (
                      overage > 0 ? (
                        <Text size="small" color="status-critical" weight="bold">
                          {USD(overage)} OVER guarantee
                        </Text>
                      ) : (
                        <Text size="small" color="dark-3">
                          {USD(Math.abs(overage))} remaining of guarantee
                        </Text>
                      )
                    ) : null}

                    <Box flex />

                    <Anchor size="small" onClick={() => toggleHoldingFeeSchedule(contract.id)}>
                      Holding Fees
                    </Anchor>
                  </Box>

                  {expandedHoldingFeesContractId === contract.id && (
                    <Box
                      pad={{ vertical: 'xsmall', horizontal: 'medium' }}
                      background="light-1"
                      gap="xxsmall"
                      border={{ side: 'top', size: '1px', color: 'light-4' }}
                    >
                      {(holdingFeeSchedules[contract.id] ?? []).map((cycle) => (
                        <Box key={cycle.cycleNumber} direction="row" align="center" gap="small">
                          <Box width="80px">
                            <Text size="xsmall">Cycle {cycle.cycleNumber}</Text>
                          </Box>
                          <Box width="120px">
                            <Text size="xsmall">{formatDateOnly(cycle.dueDate)}</Text>
                          </Box>
                          <Box width="80px">
                            <Text
                              size="xsmall"
                              weight="bold"
                              color={
                                cycle.status === 'Overdue'
                                  ? 'status-critical'
                                  : cycle.status === 'Paid'
                                    ? 'status-ok'
                                    : 'dark-3'
                              }
                            >
                              {cycle.status}
                            </Text>
                          </Box>
                          {(cycle.status === 'Due' || cycle.status === 'Overdue') && (
                            <Box direction="row" gap="xsmall">
                              <Anchor
                                size="xsmall"
                                onClick={() =>
                                  updateHoldingFeeCycle(contract.id, cycle.cycleNumber, HoldingFeeCycleStatus.PAID)
                                }
                              >
                                Mark Paid
                              </Anchor>
                              <Anchor
                                size="xsmall"
                                onClick={() =>
                                  updateHoldingFeeCycle(contract.id, cycle.cycleNumber, HoldingFeeCycleStatus.WAIVED)
                                }
                              >
                                Waive
                              </Anchor>
                            </Box>
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              );
            })
          ) : (
            <Box pad="medium" align="center">
              <Text color="dark-3">No cast assigned to this commercial yet.</Text>
            </Box>
          )}
        </CardBody>

        {/* Footer */}
        <CardFooter justify="end" gap="small" background="light-2" pad="small">
          <AddNewButton
            pad="none"
            icon={
              <TalonTip content={<Text>Add Edit Data</Text>}>
                <Cut />
              </TalonTip>
            }
            onClick={() => setShowAddEditModal(true)}
          />
          {commercial && commercial.edits && commercial.edits.length > 0 && (
            <Button
              icon={<Inspect />}
              size="small"
              label="Edit Reviews"
              onClick={() => setShowCommercialEditReviewModal(true)}
              tip="View and manage edit reviews for all edits"
            />
          )}
          <Delete
            onClick={() => deleteEdits()}
            name="the selected Edits"
            disabled={!selectedEditIds.length}
            tip={{
              content: 'Remove Selected Edits',
              dropProps: {
                background: 'light-1',
                round: borderRadiusSize,
                pad: 'small',
                margin: 'small',
                border: { size: '1px', color: 'light-4' },
              },
              plain: true,
            }}
          />
        </CardFooter>

        {commercial && showCommercialEditReviewModal && (
          <CommercialEditReviewModal
            commercial={commercial}
            setShowCommercialEditReviewModal={setShowCommercialEditReviewModal}
            initialEditId={initialEditId}
          />
        )}

        {commercial && showAddEditModal && (
          <CreateEditModal commercial={commercial} addEdit={addEdit} setShowModal={setShowAddEditModal} />
        )}

        {pendingTypeChange && (
          <ConfirmModal
            content="Warning: changing the Commercial type will reset all existing Usages. Continue?"
            onConfirm={() => onConfirmCommercialType(pendingTypeChange)}
            onCancel={() => setPendingTypeChange(undefined)}
          />
        )}
      </Card>
    );
  }
);
